import React, { Dispatch } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip
} from '@mui/material';
import { Download as DownloadIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { styles } from './styles';
import { useQueryClient } from '@tanstack/react-query';
import DocTypeIcon from '@/components/NewUI/Components/Utilities/DocTypeIcon';
import dayjs from 'dayjs';
import AttachmentPreviewModal from '../Application/Modals/AttachmentPreviewModal';
import { ExternalViewAction, ExternalViewState } from './reducer';
import { getAttachmentType } from '../utils/attachment-type';
import { IAttachment } from '../Application/types';
import { AttachmentType } from '../Application/config';

const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const BASE_URL = window.location.origin;

export default function Attachments({
  state,
  dispatch
}: {
  state: ExternalViewState;
  dispatch: Dispatch<ExternalViewAction>;
}) {
  const queryClient = useQueryClient();
  const externalViewData = queryClient.getQueryData<any>(['externalViewData']);
  const attachments = externalViewData?.application?.attachments;

  const openPreviewModal = (attachment: any) => {
    const attachmentType = getAttachmentType(attachment as IAttachment);
    if (attachmentType === AttachmentType.Video) {
      window.open(`${BASE_URL}${attachment.public_url}`, '_blank');
    } else {
      dispatch({ type: 'SET_SELECTED_ATTACHMENT', payload: attachment });
      dispatch({ type: 'SET_PREVIEW_ATTACHMENT', payload: true });
    }
  };

  return (
    <Stack sx={styles.sectionContainer}>
      <Stack sx={{ fontSize: '16px', fontWeight: 600 }}>Attachments</Stack>
      <TableContainer sx={{ boxShadow: 'none', overflowX: 'auto' }} component={Paper}>
        <Table sx={styles.tableWrapper}>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                Attachment Title
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Attached by</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments.map((attachment: any) => {
              const createdAt = attachment.created_at;
              const attachmentTitle = attachment.attachment_title;
              const attachedBy = attachment.attached_by;
              const attachmentType = getAttachmentType(attachment as IAttachment);
              return (
                <TableRow key={attachment.id}>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' }
                      }}
                    >
                      <Box sx={styles.fileIcon} onClick={() => openPreviewModal(attachment)}>
                        {DocTypeIcon(attachment.attached_file_name.split('.').pop())}
                      </Box>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={styles.fileName} onClick={() => openPreviewModal(attachment)}>
                          {attachment.attached_file_name}
                        </Box>
                        <Box
                          sx={{
                            color: '#999999',
                            fontSize: { xs: '12px', sm: '14px' }
                          }}
                        >
                          {createdAt
                            ? dayjs(createdAt).tz(browserTimeZone).format('Do MMMM YYYY[,] h:mma')
                            : '-'}
                        </Box>
                        {/* Mobile-only info */}
                        <Box
                          sx={{
                            display: { xs: 'block', sm: 'none' },
                            mt: 1,
                            fontSize: '13px',
                            color: '#666666'
                          }}
                        >
                          <Box sx={{ mb: 0.5 }}>
                            <strong>Title:</strong>{' '}
                            {attachmentTitle === 'N/A' ? '-' : attachmentTitle}
                          </Box>
                          <Box>
                            <strong>Attached by:</strong> {attachedBy}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...styles.fileAttachedBy,
                      display: { xs: 'none', sm: 'table-cell' }
                    }}
                  >
                    {attachmentTitle === 'N/A' ? '-' : attachmentTitle}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...styles.fileAttachedBy,
                      display: { xs: 'none', sm: 'table-cell' }
                    }}
                  >
                    {attachedBy}
                  </TableCell>
                  <TableCell align={'right'}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                      }}
                    >
                      <Tooltip title={'Preview'} placement={'top'} arrow>
                        <IconButton
                          sx={styles.tableActionIcon}
                          onClick={() => openPreviewModal(attachment)}
                        >
                          <VisibilityIcon sx={styles.preview} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={'Download'} placement={'top'} arrow>
                        <IconButton
                          sx={styles.tableActionIcon}
                          href={
                            attachmentType === 'video'
                              ? attachment.attached_url
                              : `${BASE_URL}/api/v4/jobs/${externalViewData.application.job.id}/applications/${externalViewData.application.id}/application_external_view/${externalViewData.id}/assets/${attachment.id}?login_hash=${externalViewData.login_hash}`
                          }
                        >
                          <DownloadIcon sx={styles.downloadIcon} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {state.previewAttachment && state.selectedAttachment && (
        <AttachmentPreviewModal
          previewOpen={state.previewAttachment}
          externalViewDispatch={dispatch}
          selectedAttachment={state.selectedAttachment}
          permissions={undefined}
        />
      )}
    </Stack>
  );
}
